import {
  BarChart,
  BarList,
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  DataTable,
  DetailPanel,
  EmptyState,
  ErrorState,
  HierarchyPath,
  MetricFields,
  MetricRow,
  type MetricRowItem,
  RecordFormDialog,
  type RecordFormField,
  ShareMix,
  Spinner,
  Stack,
  TextLink,
} from '@olytic/foundation';
import { BillingTreatmentCard, CatalogTree, ProductStructure, type ProductStructureOption } from '@olytic/foundation/cpq';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { formatCurrency, formatNumber, humanize } from '../objects/format';
import { recordPath } from '../objects/routes';
import {
  loadProductPage,
  moveProducts,
  orderOptions,
  type ProductOption,
  type ProductPageData,
  removeOption,
  saveOption,
} from './productPageApi';

const FAMILY: Record<string, string> = { consulting: 'Consulting', studio: 'Studio', software: 'Software', agents: 'AI Agents' };
const STREAM: Record<string, string> = { workstream: 'Workstream', consulting: 'Consulting', studio: 'Studio', software: 'Software', agents: 'AI Agents' };
const SCHEME: Record<string, string> = { flat: 'Flat', per_hour: 'Per Hour', grid: 'Grid', included: 'Included' };
const METHOD: Record<string, string> = { milestone: 'Milestone', installments: 'Installments', recurring: 'Recurring', on_completion: 'On Completion', on_purchase: 'On Purchase' };
const FREQUENCY: Record<string, string> = { one_time: 'One-Time', monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual' };
const TIMING: Record<string, string> = { in_advance: 'In Advance', in_arrears: 'In Arrears' };
const TRIGGER: Record<string, string> = { on_acceptance: 'On Acceptance', milestone: 'Milestone', month_offset: 'Month Offset', completion: 'On Completion' };
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const yes = (v: boolean) => (v ? 'Yes' : 'No');
const kilo = (n: number) => `$${Number((n / 1000).toFixed(n < 10_000 ? 1 : 0))}k`;
const compact = (n: number) => (n >= 1_000_000 ? `$${Number((n / 1_000_000).toFixed(2))}M` : n >= 10_000 ? `$${Math.round(n / 1000)}k` : formatCurrency(n));
const monthLabel = (m: string) => `${MONTH[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;
const messageOf = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

type Navigate = (href: string) => void;

/** The product page's payload, read again whenever `version` changes. */
export function useProductPage(id: string, enabled: boolean, version: number) {
  const [data, setData] = useState<ProductPageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [own, setOwn] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    loadProductPage(id)
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e: unknown) => { if (live) setError(messageOf(e, 'The product page could not be loaded.')); });
    return () => { live = false; };
  }, [id, enabled, version, own]);
  const reload = useCallback(() => setOwn((n) => n + 1), []);
  return { data, error, reload };
}

function Linked({ href, onNavigate, children }: { href: string; onNavigate: Navigate; children: ReactNode }) {
  return (
    <TextLink href={href} onClick={(e) => { if (!e.metaKey && !e.ctrlKey && !e.shiftKey) { e.preventDefault(); onNavigate(href); } }}>
      {children}
    </TextLink>
  );
}

// ── Summary ───────────────────────────────────────────────────────────────

/** The cards under the header: where the product sits, who delivers it, and how it is priced. */
export function ProductSummary({ data, record }: { data: ProductPageData; record: Record<string, unknown> }): JSX.Element {
  const group = data.catalog.flatMap((f) => f.groups).find((g) => g.id === data.product.group_id);
  const dp = data.pricing.default_price;
  const hours = Number(record.estimated_hours ?? 0);
  const amount = !dp ? '—'
    : dp.grid ? (dp.grid.min_amount === null || dp.grid.max_amount === null ? '—' : `${kilo(dp.grid.min_amount)} – ${kilo(dp.grid.max_amount)}`)
    : dp.scheme === 'included' ? 'Included'
    : `${formatCurrency(dp.amount)}${dp.scheme === 'per_hour' ? '/h' : dp.period === 'month' ? ' a month' : dp.period === 'year' ? ' a year' : ''}`;
  const metrics: MetricRowItem[] = [
    {
      key: 'taxonomy',
      label: 'Taxonomy',
      content: (
        <HierarchyPath
          label="Taxonomy"
          levels={[
            { key: 'family', kind: 'family', label: FAMILY[group?.family ?? ''] ?? group?.family ?? '—' },
            { key: 'group', kind: 'group', label: group?.name ?? '—' },
            { key: 'product', kind: 'product', label: data.product.name },
          ]}
        />
      ),
    },
    {
      key: 'resourcing',
      label: 'Resourcing',
      content: (
        <ShareMix
          label="Resourcing"
          value={formatNumber(hours)}
          unit="h"
          segments={data.roleSplits.map((r) => ({ key: r.id, label: r.name, share: r.share_pct }))}
        />
      ),
    },
    {
      key: 'pricing-method',
      label: 'Pricing Method',
      content: (
        <MetricFields
          label="Pricing Method"
          fields={dp ? [
            { key: 'price', label: 'Default Price', value: dp.label ?? '—' },
            { key: 'model', label: 'Selling Model', value: dp.selling_model.name },
            { key: 'scheme', label: 'Scheme', value: SCHEME[dp.scheme] ?? dp.scheme },
            dp.grid
              ? { key: 'grid', label: 'Price Grid', value: dp.grid.dimensions.join(' × ') || dp.grid.name }
              : { key: 'amount', label: 'Amount', value: amount },
            { key: 'other', label: 'Other Prices', value: String(data.pricing.other_prices) },
            ...(dp.grid ? [{ key: 'range', label: 'Range', value: amount }] : []),
          ] : [{ key: 'price', label: 'Default Price', value: '—' }]}
        />
      ),
    },
  ];
  return <MetricRow metrics={metrics} layout="wrap" surface="inverse" aria-label="Product summary" />;
}

// ── Catalog ───────────────────────────────────────────────────────────────

/** The whole catalog as a tree, on this product's branch; products drag to another group or place. */
export function CatalogTab({ data, onNavigate, onChanged, onError }: { data: ProductPageData; onNavigate: Navigate; onChanged: () => void; onError: (message: string) => void }): JSX.Element {
  const groups = data.catalog.flatMap((f) => f.groups);
  const group = groups.find((g) => g.id === data.product.group_id);
  const place = group ? group.products.findIndex((p) => p.id === data.product.id) + 1 : 0;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(19rem,1fr)]">
      <CatalogTree
        rootLabel="Olytic Catalog"
        currentId={data.product.id}
        families={data.catalog.map((f) => ({
          id: f.family,
          name: FAMILY[f.family] ?? f.family,
          groups: f.groups.map((g) => ({
            id: g.id,
            code: g.code,
            name: g.name,
            products: g.products.map((p) => ({
              id: p.id,
              code: p.code,
              name: p.name,
              structure: p.structure === 'bundle' ? 'bundle' as const : 'single' as const,
              isComponent: p.is_component,
              status: p.status as 'active' | 'draft' | 'retired',
            })),
          })),
        }))}
        renderProduct={(p) => <Linked href={recordPath('products', p.id)} onNavigate={onNavigate}>{p.name}</Linked>}
        onMove={(ids, groupId, beforeId) => {
          moveProducts(ids, groupId, beforeId).then(onChanged).catch((e: unknown) => onError(messageOf(e, 'The products could not be moved.')));
        }}
      />
      <Card>
        <CardHeader title="Where It Sits" headingLevel={3} />
        <CardBody>
          <DetailPanel
            fields={group ? [
              { key: 'family', label: 'Family', value: FAMILY[group.family] ?? group.family },
              { key: 'group', label: 'Group', value: <Linked href={recordPath('product-groups', group.id)} onNavigate={onNavigate}>{`${group.code} · ${group.name}`}</Linked> },
              { key: 'stream', label: 'Revenue Stream', value: STREAM[group.revenue_stream] ?? group.revenue_stream },
              { key: 'order', label: 'Order in Group', value: place ? `${place} of ${group.products.length}` : '—' },
              { key: 'required', label: 'Group Required', value: yes(group.required) },
              { key: 'max', label: 'Group Max per Quote', value: group.max_per_quote ?? '—' },
              { key: 'part-of', label: 'Part Of', value: data.partOf.length ? data.partOf.map((b) => b.name).join(', ') : '—' },
            ] : [{ key: 'group', label: 'Group', value: '—' }]}
          />
        </CardBody>
      </Card>
    </div>
  );
}

// ── Structure ─────────────────────────────────────────────────────────────

type StructureOption = ProductStructureOption & { source: ProductOption; own: boolean };

const toStructure = (o: ProductOption, own: boolean): StructureOption => ({
  id: o.id,
  code: o.product.code,
  name: o.product.name,
  group: o.option_group ?? 'Options',
  role: o.included ? 'included' : o.required ? 'required' : 'optional',
  source: o,
  own,
  children: o.children.map((c) => toStructure(c, false)),
});

const quantityText = (o: ProductOption) => {
  if (o.min_quantity !== null && o.min_quantity === o.max_quantity) return `Quantity ${formatNumber(o.min_quantity)}`;
  const range = o.max_quantity !== null ? `Quantity ${formatNumber(o.min_quantity ?? 1)}–${formatNumber(o.max_quantity)}` : `Quantity ${formatNumber(o.min_quantity ?? 1)}+`;
  return o.default_quantity === null ? range : `${range}, Default ${formatNumber(o.default_quantity)}`;
};

const priceText = (o: ProductOption) => {
  if (o.included) return 'Included';
  const p = o.default_price;
  if (!p) return '';
  if (p.price_grid) return p.price_grid;
  return `${formatCurrency(p.amount)}${p.scheme === 'per_hour' ? '/h' : p.period === 'month' ? ' a month' : p.period === 'year' ? ' a year' : ''}`;
};

const flatten = (os: ProductOption[]): ProductOption[] => os.flatMap((o) => [o, ...flatten(o.children)]);

/** A bundle's options: a tree that reorders by drag, the focused option's fields, and the default configuration. */
export function StructureTab({ data, record, onNavigate, onChanged, onError }: {
  data: ProductPageData;
  record: Record<string, unknown>;
  onNavigate: Navigate;
  onChanged: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const [focus, setFocus] = useState<string | null>(null);
  const [form, setForm] = useState<'add' | 'edit' | null>(null);
  const [busy, setBusy] = useState(false);
  const all = flatten(data.options);
  const chosen = all.find((o) => o.id === focus) ?? null;
  const own = chosen ? data.options.some((o) => o.id === chosen.id) : false;
  const groupNames = [...new Set(data.options.map((o) => o.option_group ?? 'Options'))];
  const productLink = (o: ProductOption) => <Linked href={recordPath('products', o.product.id)} onNavigate={onNavigate}>{o.product.name}</Linked>;

  const write = (task: Promise<unknown>, failure: string) => {
    setBusy(true);
    task.then(onChanged).catch((e: unknown) => onError(messageOf(e, failure))).finally(() => setBusy(false));
  };

  // A new order of this bundle's own options, each with its group.
  const reorder = (next: { id: string; option_group: string | null }[]) => write(orderOptions(data.product.id, next), 'The options could not be reordered.');
  const current = () => data.options.map((o) => ({ id: o.id, option_group: o.option_group }));
  const move = (ids: string[], group: string, beforeId: string | null) => {
    const moved = current().filter((o) => ids.includes(o.id)).map((o) => ({ ...o, option_group: group }));
    const rest = current().filter((o) => !ids.includes(o.id));
    let at = beforeId ? rest.findIndex((o) => o.id === beforeId) : -1;
    if (at < 0) { const last = rest.map((o) => o.option_group ?? 'Options').lastIndexOf(group); at = last < 0 ? rest.length : last + 1; }
    rest.splice(at, 0, ...moved);
    reorder(rest);
  };
  const moveGroup = (group: string, beforeGroup: string | null) => {
    const inGroup = (o: { option_group: string | null }) => (o.option_group ?? 'Options') === group;
    const block = current().filter(inGroup);
    const rest = current().filter((o) => !inGroup(o));
    const at = beforeGroup ? rest.findIndex((o) => (o.option_group ?? 'Options') === beforeGroup) : -1;
    rest.splice(at < 0 ? rest.length : at, 0, ...block);
    reorder(rest);
  };

  const dp = data.pricing.default_price;
  type BuildRow = { id: string; name: ReactNode; qty: number | null; included: boolean | null; price: ReactNode; hours: number; own: boolean };
  const ownIds = new Set(data.options.map((o) => o.id));
  const rows: BuildRow[] = [
    {
      id: 'bundle', name: data.product.name, qty: 1, included: null, own: false, hours: Number(record.estimated_hours ?? 0),
      price: dp ? <Linked href={recordPath('prices', dp.id)} onNavigate={onNavigate}>{dp.label ?? 'Default Price'}</Linked> : '—',
    },
    ...all.map((o): BuildRow => ({
      id: o.id, name: productLink(o), qty: o.default_quantity, included: o.included, own: ownIds.has(o.id),
      hours: (o.estimated_hours ?? 0) * (o.default_quantity ?? 0),
      price: o.included ? 'Included' : o.default_price ? <Linked href={recordPath('prices', o.default_price.id)} onNavigate={onNavigate}>{o.default_price.label ?? priceText(o)}</Linked> : '—',
    })),
  ];
  const total: BuildRow = { id: 'total', name: 'Total', qty: null, included: null, price: '', own: false, hours: rows.reduce((t, r) => t + r.hours, 0) };
  const columns: ColumnDef<BuildRow>[] = [
    { key: 'name', label: 'Line', sortable: false, render: (r) => r.name },
    {
      key: 'qty', label: 'Default Quantity', align: 'right', sortable: false,
      editor: { kind: 'number', min: 0.01, nullable: true }, editable: (r) => r.own,
      render: (r) => (r.id === 'total' ? '' : r.qty === null ? 'Not Added' : formatNumber(r.qty)),
    },
    { key: 'included', label: 'Included', sortable: false, editor: { kind: 'boolean' }, editable: (r) => r.own, render: (r) => (r.included === null ? '' : yes(r.included)) },
    { key: 'price', label: 'Default Price', sortable: false, render: (r) => r.price },
    { key: 'hours', label: 'Hours', align: 'right', sortable: false, render: (r) => `${formatNumber(r.hours)} h` },
  ];

  const ruleFields: RecordFormField[] = [
    { key: 'option_group', label: 'Option Group', editor: { kind: 'select', options: groupNames.map((g) => ({ value: g, label: g })), nullable: true } },
    { key: 'new_group', label: 'New Option Group', editor: { kind: 'text', maxLength: 100 } },
    { key: 'required', label: 'Required', editor: { kind: 'boolean' } },
    { key: 'included', label: 'Included', editor: { kind: 'boolean' } },
    { key: 'min_quantity', label: 'Min Quantity', editor: { kind: 'number', min: 0.01 } },
    { key: 'default_quantity', label: 'Default Quantity', editor: { kind: 'number', min: 0.01 } },
    { key: 'max_quantity', label: 'Max Quantity', editor: { kind: 'number', min: 0.01 } },
  ];
  const candidates = data.catalog.flatMap((f) => f.groups).flatMap((g) => g.products)
    .filter((p) => p.id !== data.product.id && p.status !== 'retired' && !data.options.some((o) => o.product.id === p.id));
  const num = (v: string | null | undefined) => (v === null || v === undefined || v === '' ? null : Number(v));
  const ruleValues = (v: Record<string, string | null>) => ({
    option_group: v.new_group?.trim() || v.option_group || null,
    required: v.required === 'true',
    included: v.included === 'true',
    min_quantity: num(v.min_quantity),
    default_quantity: num(v.default_quantity),
    max_quantity: num(v.max_quantity),
  });

  return (
    <Stack gap="lg">
      <section>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="m-0 text-base font-medium">Product Structure</h3>
          <Button variant="neutral" size="sm" disabled={busy} onClick={() => setForm('add')}>Add Option</Button>
        </div>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <ProductStructure<StructureOption>
            root={{ code: data.product.code, name: data.product.name, facts: ['Bundle', ...(dp?.label ? [dp.label] : []), `${formatNumber(Number(record.estimated_hours ?? 0))} h`] }}
            options={data.options.map((o) => toStructure(o, true))}
            focus={focus}
            onFocus={(o) => setFocus(o?.id ?? null)}
            renderName={(o) => productLink(o.source)}
            facts={(o) => [quantityText(o.source), priceText(o.source), o.source.estimated_hours ? `${formatNumber(o.source.estimated_hours)} h` : ''].filter(Boolean)}
            canMove={(o) => o.own && !busy}
            onMove={move}
            onMoveGroup={moveGroup}
          />
          {chosen ? (
            <Card>
              <CardHeader
                title={chosen.product.name}
                headingLevel={3}
                actions={own ? (
                  <>
                    <Button variant="neutral" size="sm" disabled={busy} onClick={() => setForm('edit')}>Edit</Button>
                    <Button variant="neutral" size="sm" disabled={busy} onClick={() => { setFocus(null); write(removeOption(chosen.id), 'The option could not be removed.'); }}>Remove</Button>
                  </>
                ) : undefined}
              />
              <CardBody>
                <DetailPanel
                  fields={[
                    { key: 'product', label: 'Product', value: <Linked href={recordPath('products', chosen.product.id)} onNavigate={onNavigate}>{`${chosen.product.code} · ${chosen.product.name}`}</Linked> },
                    { key: 'group', label: 'Option Group', value: chosen.option_group ?? '—' },
                    { key: 'required', label: 'Required', value: yes(chosen.required) },
                    { key: 'included', label: 'Included', value: yes(chosen.included) },
                    { key: 'min', label: 'Min Quantity', value: chosen.min_quantity === null ? '—' : formatNumber(chosen.min_quantity) },
                    { key: 'default', label: 'Default Quantity', value: chosen.default_quantity === null ? '—' : formatNumber(chosen.default_quantity) },
                    { key: 'max', label: 'Max Quantity', value: chosen.max_quantity === null ? '—' : formatNumber(chosen.max_quantity) },
                    { key: 'sort', label: 'Sort Order', value: own ? String(chosen.sort_order) : '—' },
                    { key: 'price', label: 'Default Price', value: chosen.default_price ? <Linked href={recordPath('prices', chosen.default_price.id)} onNavigate={onNavigate}>{chosen.default_price.label ?? priceText(chosen)}</Linked> : '—' },
                    { key: 'hours', label: 'Estimated Hours', value: chosen.estimated_hours ? `${formatNumber(chosen.estimated_hours)} h` : '—' },
                  ]}
                />
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardHeader title={data.product.name} headingLevel={3} />
              <CardBody>
                <DetailPanel
                  fields={[
                    { key: 'price', label: 'Default Price', value: dp ? <Linked href={recordPath('prices', dp.id)} onNavigate={onNavigate}>{dp.label ?? 'Default Price'}</Linked> : '—' },
                    { key: 'hours', label: 'Estimated Hours', value: `${formatNumber(Number(record.estimated_hours ?? 0))} h` },
                    { key: 'options', label: 'Options', value: String(all.length) },
                    { key: 'required', label: 'Required', value: String(all.filter((o) => o.required && !o.included).length) },
                    { key: 'included', label: 'Included', value: String(all.filter((o) => o.included).length) },
                    { key: 'optional', label: 'Optional', value: String(all.filter((o) => o.default_quantity === null).length) },
                  ]}
                />
              </CardBody>
            </Card>
          )}
        </div>
      </section>
      <section>
        <h3 className="m-0 mb-2 text-base font-medium">Default Configuration</h3>
        <DataTable<BuildRow>
          columns={columns}
          rows={[...rows, total]}
          getRowId={(r) => r.id}
          highlightedId="total"
          density="compact"
          onCellSave={(row, key, value) => {
            if (!row.own) return;
            if (key === 'qty') write(saveOption(row.id, { default_quantity: value === null || value === '' ? null : Number(value) }), 'The default quantity could not be saved.');
            if (key === 'included') write(saveOption(row.id, { included: value === 'true' }), 'The option could not be saved.');
          }}
        />
      </section>
      <RecordFormDialog
        key={`add-${data.options.length}`}
        open={form === 'add'}
        onOpenChange={(o) => { if (!o) setForm(null); }}
        title="Add Option"
        fields={[
          { key: 'option_product_id', label: 'Product', required: true, editor: { kind: 'select', options: candidates.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` })) } },
          ...ruleFields,
        ]}
        initialValues={{ required: 'false', included: 'false' }}
        submitLabel="Add"
        onSubmit={async (v) => {
          await saveOption(null, { bundle_product_id: data.product.id, option_product_id: v.option_product_id, ...ruleValues(v), sort_order: data.options.length + 1 });
          setForm(null);
          onChanged();
        }}
      />
      {chosen && own ? (
        <RecordFormDialog
          key={`edit-${chosen.id}`}
          open={form === 'edit'}
          onOpenChange={(o) => { if (!o) setForm(null); }}
          title={`Edit ${chosen.product.name}`}
          fields={ruleFields}
          initialValues={{
            option_group: chosen.option_group ?? '',
            required: String(chosen.required),
            included: String(chosen.included),
            min_quantity: chosen.min_quantity === null ? '' : String(chosen.min_quantity),
            default_quantity: chosen.default_quantity === null ? '' : String(chosen.default_quantity),
            max_quantity: chosen.max_quantity === null ? '' : String(chosen.max_quantity),
          }}
          submitLabel="Save"
          onSubmit={async (v) => {
            await saveOption(chosen.id, ruleValues(v));
            setForm(null);
            onChanged();
          }}
        />
      ) : null}
    </Stack>
  );
}

// ── Billing ───────────────────────────────────────────────────────────────

/** The product's billing policy and its treatments, read-only: they are edited on the policy. */
export function BillingTab({ data, onNavigate }: { data: ProductPageData; onNavigate: Navigate }): JSX.Element {
  const policy = data.billing.policy;
  if (!policy) return <EmptyState title="No billing policy" description="Choose one in Details." />;
  const href = recordPath('billing-policies', policy.id);
  return (
    <Stack gap="md">
      <div className="flex items-center justify-between gap-3">
        <h3 className="m-0 text-base font-medium">Billing Policy · <Linked href={href} onNavigate={onNavigate}>{policy.name}</Linked></h3>
        <Button variant="neutral" size="sm" onClick={() => onNavigate(href)}>Open Policy</Button>
      </div>
      {data.billing.treatments.length ? data.billing.treatments.map((t) => (
        <BillingTreatmentCard
          key={t.id}
          treatment={{
            name: t.name,
            method: METHOD[t.method] ?? t.method,
            frequency: FREQUENCY[t.billing_frequency] ?? t.billing_frequency,
            timing: t.billing_timing ? TIMING[t.billing_timing] ?? t.billing_timing : null,
            isDefault: t.is_default,
            lines: t.quote_lines + t.order_lines,
            items: t.items.map((i) => ({ sequence: i.sequence, label: i.label, trigger: TRIGGER[i.trigger] ?? i.trigger, monthOffset: i.month_offset, percent: i.percent })),
          }}
        />
      )) : <EmptyState title="No treatments" description="The policy has no active treatments." />}
    </Stack>
  );
}

// ── Sales ─────────────────────────────────────────────────────────────────

type OpenLine = ProductPageData['sales']['open_lines'][number];

/** Where it sells: revenue scheduled from its orders, quoted by company size, and the open quote lines. */
export function SalesTab({ data, onNavigate }: { data: ProductPageData; onNavigate: Navigate }): JSX.Element {
  const { sales } = data;
  const link = (object: Parameters<typeof recordPath>[0], id: string, text: string) => <Linked href={recordPath(object, id)} onNavigate={onNavigate}>{text}</Linked>;
  const columns: ColumnDef<OpenLine>[] = [
    { key: 'quote', label: 'Quote', sortable: false, render: (r) => link('quotes', r.quote.id, r.quote.name) },
    { key: 'account', label: 'Account', sortable: false, render: (r) => link('accounts', r.account.id, r.account.name) },
    { key: 'opportunity', label: 'Opportunity', sortable: false, render: (r) => (r.opportunity ? link('opportunities', r.opportunity.id, r.opportunity.name) : '—') },
    { key: 'stage', label: 'Stage', sortable: false, render: (r) => (r.stage ? humanize(r.stage) : '—') },
    { key: 'company_size', label: 'Company Size', sortable: false, render: (r) => r.company_size ?? '—' },
    { key: 'quantity', label: 'Quantity', align: 'right', sortable: false, render: (r) => formatNumber(r.quantity) },
    { key: 'total', label: 'Total', align: 'right', sortable: false, render: (r) => (r.total === null ? '—' : `${formatCurrency(r.total)}${r.billing === 'monthly' ? ' a month' : ''}`) },
  ];
  return (
    <Stack gap="lg">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(19rem,1fr)]">
        <BarChart
          title="Revenue Scheduled"
          data={sales.scheduled.map((r) => ({ ...r, month: monthLabel(r.month) }))}
          xKey="month"
          config={{ one_time: { label: 'One-Time', color: 'var(--chart-1)' }, recurring: { label: 'Recurring', color: 'var(--chart-3)' } }}
          stacked
          height={220}
          valueFormatter={compact}
        />
        <Card>
          <CardHeader title="Quoted by Company Size" headingLevel={3} />
          <CardBody>
            {sales.by_company_size.length ? (
              <BarList
                items={sales.by_company_size.map((s) => ({ key: s.key ?? 'none', label: s.label ?? 'No Size', value: s.one_time + s.recurring }))}
                valueFormatter={compact}
                layout="inline"
              />
            ) : 'None'}
          </CardBody>
        </Card>
      </div>
      <section>
        <h3 className="m-0 mb-2 text-base font-medium">Open Quote Lines</h3>
        <DataTable<OpenLine> columns={columns} rows={sales.open_lines} getRowId={(r) => r.id} empty="None" density="compact" />
      </section>
    </Stack>
  );
}

/** A tab's body while the page payload loads or after it failed. */
export function PagePending({ error, onRetry }: { error: string | null; onRetry: () => void }): JSX.Element {
  return error ? <ErrorState title="Could not load this tab" description={error} onRetry={onRetry} /> : <Spinner label="Loading…" />;
}
